import { consumer, inject } from "@spawn/core";
import { GoldenBonusService, TargetSymbol } from "@spawn/golden-bonus";
import { LineStepperModule, showSymbolsOnSpotlight } from "@spawn/slot-machine";
import { BaseSymbolTypes } from "../services/symbolService";

@consumer
export class NrgLineStepperModule extends LineStepperModule {
	@inject protected readonly goldenBonusService!: GoldenBonusService;

	/**
	 * Mostly the same as the original line stepper module, but with some changes to highlight the scatters and bonus symbols.
	 */
	public override async start(): Promise<void> {
		const slotFace = this.reelController.getSlotFace();

		// Get all symbol coordinates that are on paylines and filter duplicates.
		const symbolCoordinates = this.filterDoubleSymbols(
			this.partyService.paylineWins
				.filter(({ winAmount }) => this.ignoreZeroWins() && winAmount && winAmount > 0)
				.flatMap((payline) => payline.symbolCoordinates)
		);

		// scatter symbols are not part of the paylines, so we need to add them manually
		const scatterCoordinates = slotFace
			.flatMap((reel, x) => reel.map((symbol, y) => (symbol === BaseSymbolTypes.Scatter ? { x, y } : null)))
			.filter(Boolean) as { x: number; y: number }[];

		if (scatterCoordinates.length >= 3) {
			// FG won so we can highlight all scatters
			symbolCoordinates.push(...scatterCoordinates);
		}

		// Get all bonus symbol coordinates to highlight them
		const bonusSymbols = this.goldenBonusService.getBonusSymbols();
		if (bonusSymbols.length) {
			for (const bonusSymbol of bonusSymbols) {
				symbolCoordinates.push({ x: bonusSymbol.position.x, y: bonusSymbol.position.y });
				this.onBonusSymbol(slotFace, bonusSymbol);
			}
		}

		// Get all symbol coordinates that uses default meta data keys.
		const symbolCoordinatesDefaults = symbolCoordinates.filter((coordinates) => {
			const symbolType = slotFace[coordinates.x][coordinates.y];
			return !this.visibleMetaDataKeysBySymbol()[symbolType];
		});

		// Highlight and play all winning symbols with default meta data keys.
		this.eventDispatcher.dispatch(
			showSymbolsOnSpotlight({
				symbolCoordinates: symbolCoordinatesDefaults,
				symbolMetaDataKeys: this.visibleMetaDataKeys(),
				loopAnimations: true,
			})
		);

		// If we need to show(or hide) specific meta data keys for each symbol type, we will do that here.
		for (const coodinates of symbolCoordinates) {
			const symbolType = slotFace[coodinates.x][coodinates.y];
			const symbolMetaDataKeys = this.visibleMetaDataKeysBySymbol()[symbolType];
			if (symbolMetaDataKeys) {
				this.eventDispatcher.dispatch(
					showSymbolsOnSpotlight({
						symbolCoordinates: [coodinates],
						symbolMetaDataKeys,
						loopAnimations: true,
					})
				);
			}
		}

		await this.lineStepperLoop?.play();
		this.onLineStepperComplete(symbolCoordinates);
	}

	protected onBonusSymbol(_slotFace: string[][], _bonusSymbol: TargetSymbol): void {}

	protected onLineStepperComplete(_symbolCoordinates: { x: number; y: number }[]): void {}
}
